import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Camera,
  User,
  Mail,
  Phone,
  MapPin,
  Lock,
  GraduationCap,
  Calendar,
  Briefcase,
  Facebook,
  Instagram,
  Linkedin,
} from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import {
  server_post_data,
  get_staff_data,
  get_designation_data,
  staff_save_update,
  APL_LINK,
} from "../serviceconnection/ServiceConnection";

// ── Outside component — no remount issue ─────────────────────────────────────
const InputField = ({
  label,
  placeholder,
  type = "text",
  icon: Icon,
  disabled,
  value,
  onChange,
  required,
}) => (
  <div className="space-y-1">
    <label className="text-xs font-semibold text-muted-foreground">
      {label}
      {required && <span className="text-destructive"> *</span>}
    </label>
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background focus-within:border-accent ${disabled ? "opacity-60" : ""}`}
    >
      {Icon && <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      <input
        type={type}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground disabled:cursor-not-allowed"
      />
    </div>
  </div>
);

const SelectField = ({ label, options, value, onChange, required }) => (
  <div className="space-y-1">
    <label className="text-xs font-semibold text-muted-foreground">
      {label}
      {required && <span className="text-destructive"> *</span>}
    </label>
    <select
      value={value ?? ""}
      onChange={onChange}
      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
    >
      <option value="">Select...</option>
      {options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>
          {o.label ?? o}
        </option>
      ))}
    </select>
  </div>
);

// ── Tabs ──────────────────────────────────────────────────────────────────────
const TABS = [
  { key: "basic", label: "Basic Info", icon: User },
  { key: "address", label: "Address", icon: MapPin },
  { key: "social", label: "Social Links", icon: Linkedin },
  { key: "access", label: "Access", icon: Briefcase },
];

const EMPTY_FORM = {
  admin_name: "",
  admin_email: "",
  admin_mobile_no: "",
  admin_other_no: "",
  admin_gender: "",
  admin_blood_group: "",
  admin_education: "",
  admin_dob_date: "",
  admin_join_date: "",
  admin_designation: "",
  admin_manual_adrress: "",
  admin_city: "",
  admin_state: "",
  admin_country: "",
  admin_zipcode: "",
  admin_temorary_adrress: "",
  admin_temorary_city: "",
  admin_temorary_state: "",
  admin_temorary_country: "",
  admin_temorary_zipcode: "",
  facebook_link: "",
  instragram_link: "",
  linkedin_link: "",
  admin_password: "",
  allow_access: "1",
  firebase_access_status: 0,
};

export default function StaffForm() {
  const { id } = useParams(); // undefined = Add, number = Edit
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(EMPTY_FORM);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saveLoading, setSaveLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const fileRef = useRef();

  const sf = useCallback(
    (key, val) => setForm((p) => ({ ...p, [key]: val })),
    [],
  );

  // ── Fetch designations ────────────────────────────────────────────────
  useEffect(() => {
    const loadDesignations = async () => {
      try {
        const fd = new FormData();
        fd.append("main_id", 0);
        const res = await server_post_data(get_designation_data, fd);
        const list = res.data?.data ?? [];
        setDesignations(
          list.map((d) => ({ value: d.primary_id, label: d.designation_name })),
        );
      } catch {
        /* silent */
      }
    };
    loadDesignations();
  }, []);

  // ── Fetch staff if edit ───────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    const loadStaff = async () => {
      try {
        const fd = new FormData();
        fd.append("main_id", id);
        const res = await server_post_data(get_staff_data, fd);
        const d = res.data?.data ?? {};
        setForm({
          admin_name: d.admin_name ?? "",
          admin_email: d.admin_email ?? "",
          admin_mobile_no: d.admin_mobile_no ?? "",
          admin_other_no: d.admin_other_no ?? "",
          admin_gender: d.admin_gender ?? "",
          admin_blood_group: d.admin_blood_group ?? "",
          admin_education: d.admin_education ?? "",
          admin_dob_date: d.admin_dob_date ?? "",
          admin_join_date: d.admin_join_date ?? "",
          admin_designation: d.admin_designation ?? "",
          admin_manual_adrress: d.admin_manual_adrress ?? "",
          admin_city: d.admin_city ?? "",
          admin_state: d.admin_state ?? "",
          admin_country: d.admin_country ?? "",
          admin_zipcode: d.admin_zipcode ?? "",
          admin_temorary_adrress: d.admin_temorary_adrress ?? "",
          admin_temorary_city: d.admin_temorary_city ?? "",
          admin_temorary_state: d.admin_temorary_state ?? "",
          admin_temorary_country: d.admin_temorary_country ?? "",
          admin_temorary_zipcode: d.admin_temorary_zipcode ?? "",
          facebook_link: d.facebook_link ?? "",
          instragram_link: d.instragram_link ?? "",
          linkedin_link: d.linkedin_link ?? "",
          admin_password: "", // never prefill password
          allow_access: d.allow_access ?? "1",
          firebase_access_status: d.firebase_access_status ?? 0,
        });
        if (d.admin_image_url) setImagePreview(APL_LINK + d.admin_image_url);
      } catch {
        showError("Failed to load staff");
      } finally {
        setLoading(false);
      }
    };
    loadStaff();
  }, [id]);

  // ── Image pick ────────────────────────────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // ── Save ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.admin_name || !form.admin_email) {
      showError("Name and Email are required");
      return;
    }
    if (!isEdit && !form.admin_password) {
      showError("Password is required for new staff");
      return;
    }
    try {
      setSaveLoading(true);
      const fd = new FormData();
      fd.append("p_id", isEdit ? id : 0);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (imageFile) fd.append("admin_image_file", imageFile);

      const res = await server_post_data(staff_save_update, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess(isEdit ? "Staff updated!" : "Staff created!");
      navigate("/staff");
    } catch {
      showError("Something went wrong");
    } finally {
      setSaveLoading(false);
    }
  };

  const initials = (form.admin_name || "S")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  if (loading)
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
        Loading...
      </div>
    );

  return (
    <div className="max-w-[1080px] mx-auto space-y-5">
      {/* ── Header ── */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/staff")}
            className="w-8 h-8 rounded-lg flex items-center justify-center border border-border hover:bg-muted text-muted-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">
              {isEdit ? "Edit Staff" : "Add Staff"}
            </h1>
            <p className="page-subtitle">
              {isEdit
                ? "Update staff member details"
                : "Create a new admin panel user"}
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saveLoading}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-60"
          style={{ background: "hsl(var(--accent))" }}
        >
          <Save className="w-4 h-4" />
          {saveLoading ? "Saving..." : isEdit ? "Update" : "Create"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5">
        {/* ── Left — Avatar card ── */}
        <div className="space-y-4">
          <div className="cms-card p-5 flex flex-col items-center gap-3">
            <div className="relative">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Staff"
                  className="w-24 h-24 rounded-full object-cover border-2 border-border"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center text-2xl font-bold text-white"
                  style={{ background: "hsl(var(--accent))" }}
                >
                  {initials}
                </div>
              )}
              <button
                onClick={() => fileRef.current?.click()}
                className="absolute bottom-0 right-0 w-7 h-7 rounded-full flex items-center justify-center text-white shadow-md"
                style={{ background: "hsl(var(--accent))" }}
              >
                <Camera className="w-3.5 h-3.5" />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-foreground">
                {form.admin_name || "New Staff"}
              </p>
              <p className="text-xs text-muted-foreground">
                {form.admin_email || "email@example.com"}
              </p>
            </div>
          </div>

          {/* Access toggle card */}
          <div className="cms-card p-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Quick Settings
            </p>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Allow Access</span>
              <button
                onClick={() =>
                  sf("allow_access", form.allow_access === "1" ? "0" : "1")
                }
                style={{
                  color:
                    form.allow_access === "1"
                      ? "hsl(var(--success))"
                      : "hsl(var(--muted-foreground))",
                }}
              >
                {form.allow_access === "1" ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="20"
                    viewBox="0 0 32 20"
                    fill="none"
                  >
                    <rect
                      width="32"
                      height="20"
                      rx="10"
                      fill="hsl(var(--success))"
                    />
                    <circle cx="22" cy="10" r="8" fill="white" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="20"
                    viewBox="0 0 32 20"
                    fill="none"
                  >
                    <rect
                      width="32"
                      height="20"
                      rx="10"
                      fill="hsl(var(--border))"
                    />
                    <circle cx="10" cy="10" r="8" fill="white" />
                  </svg>
                )}
              </button>
            </div>
            
          </div>
        </div>

        {/* ── Right — Tabs ── */}
        <div className="section-panel p-0">
          {/* Tab bar */}
          <div className="flex border-b border-border overflow-x-auto px-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap -mb-px ${
                  activeTab === t.key
                    ? "border-accent text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <t.icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            ))}
          </div>

          <div className="section-panel-body space-y-4">
            {/* ── Basic ── */}
            {activeTab === "basic" && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label="Full Name"
                    placeholder="Staff full name"
                    icon={User}
                    required
                    value={form.admin_name}
                    onChange={(e) => sf("admin_name", e.target.value)}
                  />
                  <InputField
                    label="Email"
                    placeholder="email@example.com"
                    icon={Mail}
                    required
                    value={form.admin_email}
                    onChange={(e) => sf("admin_email", e.target.value)}
                    disabled={isEdit}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label="Mobile No"
                    placeholder="+91 98765 43210"
                    icon={Phone}
                    value={form.admin_mobile_no}
                    onChange={(e) => sf("admin_mobile_no", e.target.value)}
                  />
                  <InputField
                    label="Other No"
                    placeholder="Alternate number"
                    icon={Phone}
                    value={form.admin_other_no}
                    onChange={(e) => sf("admin_other_no", e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SelectField
                    label="Designation"
                    required
                    options={designations}
                    value={form.admin_designation}
                    onChange={(e) => sf("admin_designation", e.target.value)}
                  />
                  <SelectField
                    label="Gender"
                    options={["Male", "Female", "Other"]}
                    value={form.admin_gender}
                    onChange={(e) => sf("admin_gender", e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <SelectField
                    label="Blood Group"
                    options={["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"]}
                    value={form.admin_blood_group}
                    onChange={(e) => sf("admin_blood_group", e.target.value)}
                  />
                  <InputField
                    label="Date of Birth"
                    type="date"
                    icon={Calendar}
                    value={form.admin_dob_date}
                    onChange={(e) => sf("admin_dob_date", e.target.value)}
                  />
                  <InputField
                    label="Join Date"
                    type="date"
                    icon={Calendar}
                    value={form.admin_join_date}
                    onChange={(e) => sf("admin_join_date", e.target.value)}
                  />
                </div>
                <InputField
                  label="Education"
                  placeholder="e.g. B.Tech Computer Science"
                  icon={GraduationCap}
                  value={form.admin_education}
                  onChange={(e) => sf("admin_education", e.target.value)}
                />
                {!isEdit && (
                  <InputField
                    label="Password"
                    type="password"
                    placeholder="Set login password"
                    icon={Lock}
                    required
                    value={form.admin_password}
                    onChange={(e) => sf("admin_password", e.target.value)}
                  />
                )}
              </>
            )}

            {/* ── Address ── */}
            {activeTab === "address" && (
              <>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Permanent Address
                </p>
                <InputField
                  label="Address"
                  placeholder="Street / Building"
                  icon={MapPin}
                  value={form.admin_manual_adrress}
                  onChange={(e) => sf("admin_manual_adrress", e.target.value)}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label="City"
                    placeholder="City"
                    icon={MapPin}
                    value={form.admin_city}
                    onChange={(e) => sf("admin_city", e.target.value)}
                  />
                  <InputField
                    label="State"
                    placeholder="State"
                    icon={MapPin}
                    value={form.admin_state}
                    onChange={(e) => sf("admin_state", e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label="Country"
                    placeholder="Country"
                    icon={MapPin}
                    value={form.admin_country}
                    onChange={(e) => sf("admin_country", e.target.value)}
                  />
                  <InputField
                    label="Zipcode"
                    placeholder="Postal code"
                    icon={MapPin}
                    value={form.admin_zipcode}
                    onChange={(e) => sf("admin_zipcode", e.target.value)}
                  />
                </div>

                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-2">
                  Temporary Address
                </p>
                <InputField
                  label="Address"
                  placeholder="Street / Building"
                  icon={MapPin}
                  value={form.admin_temorary_adrress}
                  onChange={(e) => sf("admin_temorary_adrress", e.target.value)}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label="City"
                    placeholder="City"
                    icon={MapPin}
                    value={form.admin_temorary_city}
                    onChange={(e) => sf("admin_temorary_city", e.target.value)}
                  />
                  <InputField
                    label="State"
                    placeholder="State"
                    icon={MapPin}
                    value={form.admin_temorary_state}
                    onChange={(e) => sf("admin_temorary_state", e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label="Country"
                    placeholder="Country"
                    icon={MapPin}
                    value={form.admin_temorary_country}
                    onChange={(e) =>
                      sf("admin_temorary_country", e.target.value)
                    }
                  />
                  <InputField
                    label="Zipcode"
                    placeholder="Postal code"
                    icon={MapPin}
                    value={form.admin_temorary_zipcode}
                    onChange={(e) =>
                      sf("admin_temorary_zipcode", e.target.value)
                    }
                  />
                </div>
              </>
            )}

            {/* ── Social ── */}
            {activeTab === "social" && (
              <>
                <InputField
                  label="LinkedIn"
                  placeholder="https://linkedin.com/in/name"
                  icon={Linkedin}
                  value={form.linkedin_link}
                  onChange={(e) => sf("linkedin_link", e.target.value)}
                />
                <InputField
                  label="Facebook"
                  placeholder="https://facebook.com/name"
                  icon={Facebook}
                  value={form.facebook_link}
                  onChange={(e) => sf("facebook_link", e.target.value)}
                />
                <InputField
                  label="Instagram"
                  placeholder="https://instagram.com/name"
                  icon={Instagram}
                  value={form.instragram_link}
                  onChange={(e) => sf("instragram_link", e.target.value)}
                />
              </>
            )}

            {/* ── Access ── */}
            {activeTab === "access" && (
              <>
                <SelectField
                  label="Designation"
                  required
                  options={designations}
                  value={form.admin_designation}
                  onChange={(e) => sf("admin_designation", e.target.value)}
                />
                <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/20">
                  {[
                    {
                      label: "Allow Panel Access",
                      key: "allow_access",
                      isStr: true,
                    },
                    {
                      label: "Firebase Access",
                      key: "firebase_access_status",
                      isStr: false,
                    },
                  ].map(({ label, key, isStr }) => (
                    <div
                      key={key}
                      className="flex items-center justify-between"
                    >
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          {label}
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          sf(
                            key,
                            isStr
                              ? form[key] === "1"
                                ? "0"
                                : "1"
                              : form[key] === 1
                                ? 0
                                : 1,
                          )
                        }
                      >
                        {(isStr ? form[key] === "1" : form[key] === 1) ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="40"
                            height="22"
                            viewBox="0 0 40 22"
                          >
                            <rect
                              width="40"
                              height="22"
                              rx="11"
                              fill="hsl(var(--success))"
                            />
                            <circle cx="29" cy="11" r="9" fill="white" />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="40"
                            height="22"
                            viewBox="0 0 40 22"
                          >
                            <rect
                              width="40"
                              height="22"
                              rx="11"
                              fill="hsl(var(--border))"
                            />
                            <circle cx="11" cy="11" r="9" fill="white" />
                          </svg>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
                {isEdit && (
                  <div className="p-3 rounded-lg border border-border bg-muted/20">
                    <p className="text-xs text-muted-foreground">
                      To change password, use the staff member's own Profile
                      Settings page.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Bottom Save */}
            <div className="flex justify-end pt-2 border-t border-border">
              <button
                onClick={handleSave}
                disabled={saveLoading}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: "hsl(var(--accent))" }}
              >
                <Save className="w-4 h-4" />
                {saveLoading
                  ? "Saving..."
                  : isEdit
                    ? "Update Staff"
                    : "Create Staff"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
